import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';
import { generateOperatingCostPDF } from '../lib/pdf';
import EmptyState, { IconChart } from '../components/EmptyState';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
};
// legacy aliases kept for modal/form compatibility
const INK    = T.ink;
const BLUE   = T.ink;
const MUTED  = T.muted;
const BORDER = T.border;

const LIST_COLS = '1fr 2fr 1.5fr 1fr 2fr 100px';

const EMPTY = { property_id: '', category: 'Heizung', amount: '', cost_date: '', notes: '' };

const CATEGORIES = [
  'Heizung', 'Wasser/Abwasser', 'Müllentsorgung', 'Hausmeister',
  'Versicherung', 'Strom (Allgemein)', 'Aufzug', 'Gartenpflege',
  'Gebäudereinigung', 'Verwaltung', 'Instandhaltung', 'Sonstiges',
];

const CATEGORY_COLOR = {
  'Heizung':           '#ef4444',
  'Wasser/Abwasser':   '#111',
  'Müllentsorgung':    '#84cc16',
  'Hausmeister':       '#f59e0b',
  'Versicherung':      '#555',
  'Strom (Allgemein)': '#f97316',
  'Aufzug':            '#06b6d4',
  'Gartenpflege':      '#10b981',
  'Gebäudereinigung':  '#14b8a6',
  'Verwaltung':        '#6366f1',
  'Instandhaltung':    '#d97706',
  'Sonstiges':         '#9ca3af',
};

const inputStyle = {
  width: '100%', border: `1px solid ${BORDER}`, borderRadius: 0,
  padding: '10px 12px', fontSize: '13px', boxSizing: 'border-box',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  color: INK, background: 'white', outline: 'none',
};

function Label({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
      <div style={{ width: '24px', height: '1px', background: INK }} />
      <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: INK }}>
        {children}
      </span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED, marginBottom: '8px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtEur(val) {
  if (!val && val !== 0) return '–';
  return `${Number(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export default function Operating() {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const [costs, setCosts]               = useState([]);
  const [properties, setProperties]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState(EMPTY);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);
  const [filterProperty, setFilterProperty] = useState('alle');
  const [filterYear, setFilterYear]         = useState('alle');
  const [view, setView]                 = useState('list');

  useEffect(() => { Promise.all([fetchCosts(), fetchProperties()]); }, []);

  async function fetchCosts() {
    const { data } = await supabase.from('operating_costs').select('*').eq('owner_id', user.id).order('cost_date', { ascending: false });
    setCosts(data || []);
    setLoading(false);
  }

  async function fetchProperties() {
    const { data } = await supabase.from('properties').select('id, name').eq('owner_id', user.id).order('name');
    setProperties(data || []);
  }

  function propertyName(id) {
    return properties.find(p => p.id === id)?.name || '–';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const payload = {
      property_id: form.property_id || null,
      category:    form.category,
      amount:      form.amount !== '' ? Number(form.amount) : null,
      cost_date:   form.cost_date || null,
      notes:       form.notes    || null,
      owner_id:    user.id,
    };

    let err;
    if (editing) {
      ({ error: err } = await supabase.from('operating_costs').update(payload).eq('id', editing).eq('owner_id', user.id));
    } else {
      ({ error: err } = await supabase.from('operating_costs').insert(payload));
    }

    if (err) { setError(err.message); setSaving(false); return; }

    await fetchCosts();
    closeForm();
    setSaving(false);
    addToast(editing ? 'Eintrag aktualisiert.' : 'Betriebskosten gespeichert.', 'success');
  }

  async function deleteCost(id) {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    await supabase.from('operating_costs').delete().eq('id', id).eq('owner_id', user.id);
    setCosts(c => c.filter(x => x.id !== id));
    addToast('Eintrag gelöscht.', 'success');
  }

  function openEdit(c) {
    setEditing(c.id);
    setForm({
      property_id: c.property_id || '',
      category:    c.category    || 'Heizung',
      amount:      c.amount      ?? '',
      cost_date:   c.cost_date   || '',
      notes:       c.notes       || '',
    });
    setError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
    setError(null);
  }

  const years = useMemo(() => {
    const ys = new Set(costs.map(c => c.cost_date?.slice(0, 4)).filter(Boolean));
    return [...ys].sort((a, b) => b - a);
  }, [costs]);

  const filtered = useMemo(() => costs.filter(c => {
    const matchProp = filterProperty === 'alle' || c.property_id === filterProperty;
    const matchYear = filterYear    === 'alle'  || (c.cost_date || '').startsWith(filterYear);
    return matchProp && matchYear;
  }), [costs, filterProperty, filterYear]);

  const totalFiltered = filtered.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const annualData = useMemo(() => {
    const year = filterYear === 'alle' ? new Date().getFullYear().toString() : filterYear;
    const yearCosts = costs.filter(c =>
      (c.cost_date || '').startsWith(year) &&
      (filterProperty === 'alle' || c.property_id === filterProperty)
    );
    const byProperty = {};
    yearCosts.forEach(c => {
      const pid = c.property_id || '__none__';
      if (!byProperty[pid]) byProperty[pid] = { total: 0, byCategory: {} };
      byProperty[pid].total += Number(c.amount) || 0;
      byProperty[pid].byCategory[c.category] = (byProperty[pid].byCategory[c.category] || 0) + (Number(c.amount) || 0);
    });
    return { year, byProperty };
  }, [costs, filterYear, filterProperty]);

  return (
    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: INK }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: 32, gap: 16,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: '-0.6px', color: T.ink }}>
            Betriebskosten
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 14, color: T.muted }}>Kosten erfassen und Jahresabrechnung erstellen.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: 'white', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
            {[['list', 'Liste'], ['annual', 'Jahresabr.']].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} style={{
                border: 'none', padding: '0 16px', height: 38, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', background: view === v ? T.ink : 'transparent',
                color: view === v ? 'white' : T.muted,
                borderRight: v === 'list' ? `1px solid ${T.border}` : 'none',
              }}>{l}</button>
            ))}
          </div>
          <button
            onClick={() => {
              const year = filterYear !== 'alle' ? filterYear : new Date().getFullYear().toString();
              const prop = properties.find(p => p.id === filterProperty);
              generateOperatingCostPDF({ property: prop || properties[0], year, costs: filtered, tenants: [], sender: profile });
            }}
            className="btn-secondary"
            style={{ fontSize: 12, height: 38 }}
          >
            PDF
          </button>
          <button
            onClick={() => { setEditing(null); setForm(EMPTY); setError(null); setShowForm(true); }}
            className="btn-primary"
            style={{ fontSize: 13, height: 40, padding: '0 20px' }}
          >
            + Kosten erfassen
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: T.muted, marginBottom: 8 }}>Einträge gesamt</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: '-1px', lineHeight: 1 }}>{costs.length}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{[...new Set(costs.map(c => c.category))].length} Kategorien</div>
        </div>
        <div style={{ background: totalFiltered > 0 ? '#f5f5f5' : 'white', border: `1.5px solid ${totalFiltered > 0 ? T.border : T.border}`, borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: totalFiltered > 0 ? T.ink : T.muted, marginBottom: 8 }}>Gefilterte Summe</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: totalFiltered > 0 ? T.ink : T.muted, letterSpacing: '-0.5px', lineHeight: 1 }}>{fmtEur(totalFiltered)}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>{filtered.length} Einträge im Filter</div>
        </div>
        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: T.muted, marginBottom: 8 }}>Objekte</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: '-1px', lineHeight: 1 }}>{properties.length}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>Erfasste Objekte</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        <select value={filterProperty} onChange={e => setFilterProperty(e.target.value)}
          style={{ ...inputStyle, width: isMobile ? '100%' : '200px', cursor: 'pointer' }}>
          <option value="alle">Alle Objekte</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          style={{ ...inputStyle, width: isMobile ? 'calc(50% - 5px)' : '140px', cursor: 'pointer' }}>
          <option value="alle">Alle Jahre</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {(filterProperty !== 'alle' || filterYear !== 'alle') && (
          <button onClick={() => { setFilterProperty('alle'); setFilterYear('alle'); }}
            style={{ background: 'none', border: `1px solid ${BORDER}`, padding: '10px 16px', fontSize: '11px', color: MUTED, cursor: 'pointer', fontFamily: 'inherit' }}>
            Zurücksetzen
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>Lädt...</span>
        </div>
      ) : view === 'list' ? (
        <ListView filtered={filtered} openEdit={openEdit} deleteCost={deleteCost} propertyName={propertyName} isMobile={isMobile} onAdd={() => setShowForm(true)} />
      ) : (
        <AnnualView annualData={annualData} properties={properties} />
      )}

      {/* Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '24px',
        }}
          onClick={e => { if (e.target === e.currentTarget) closeForm(); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '28px 32px 20px', borderBottom: `1px solid ${BORDER}` }}>
              <Label>{editing ? 'Kosten bearbeiten' : 'Betriebskosten erfassen'}</Label>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 900, margin: 0, letterSpacing: '-0.5px' }}>
                  {editing ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
                </h2>
                <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: MUTED, lineHeight: 1 }}>×</button>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '28px 32px 32px' }}>
              {error && (
                <div style={{ border: `1px solid #fca5a5`, background: '#fef2f2', padding: '12px 16px', fontSize: '12px', color: '#b91c1c', marginBottom: '20px' }}>
                  Fehler: {error}
                </div>
              )}

              <Field label="Objekt *">
                <select required value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">– Objekt auswählen –</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label="Kategorie *">
                  <select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Betrag (€) *">
                  <input type="number" min="0" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <Field label="Datum">
                <input type="date" value={form.cost_date} onChange={e => setForm({ ...form, cost_date: e.target.value })} style={inputStyle} />
              </Field>

              <Field label="Notizen">
                <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Rechnungsnummer, Anmerkungen..." style={{ ...inputStyle, resize: 'vertical' }} />
              </Field>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={saving} style={{
                  flex: 1, background: saving ? MUTED : INK, color: 'white', border: 'none',
                  padding: '13px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px',
                  textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                }}>
                  {saving ? 'Speichert...' : 'Speichern'}
                </button>
                <button type="button" onClick={closeForm} style={{
                  background: 'none', border: `1px solid ${BORDER}`, color: INK,
                  padding: '13px 20px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px',
                  textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ListView({ filtered, openEdit, deleteCost, propertyName, isMobile, onAdd }) {
  if (filtered.length === 0) {
    return (
      <EmptyState
        icon={<IconChart />}
        title="Keine Betriebskosten"
        description="Erfassen Sie Betriebskosten wie Strom, Wasser oder Versicherung für Ihre Objekte."
        action="Kosten erfassen"
        onAction={onAdd}
      />
    );
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map(c => (
          <div key={c.id} style={{ border: `1px solid ${BORDER}`, background: 'white', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', background: CATEGORY_COLOR[c.category] || MUTED, flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: 700 }}>{c.category}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '-0.3px' }}>{fmtEur(c.amount)}</span>
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{propertyName(c.property_id)}</div>
            <div style={{ fontSize: '11px', color: MUTED, marginBottom: '8px' }}>
              {fmt(c.cost_date)}{c.notes ? ` · ${c.notes}` : ''}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: BLUE, padding: 0, fontFamily: 'inherit' }}>Bearbeiten</button>
              <button onClick={() => deleteCost(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: MUTED, padding: 0, fontFamily: 'inherit' }}>Löschen</button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${BORDER}`, background: 'white' }}>
      <div className="immo-thead" style={{ display: 'grid', gridTemplateColumns: LIST_COLS, padding: '12px 28px', borderBottom: `1px solid ${BORDER}` }}>
        {['Datum', 'Objekt', 'Kategorie', 'Betrag', 'Notizen', ''].map(h => (
          <span key={h} style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
        ))}
      </div>
      {filtered.map((c, i) => (
        <div key={c.id} className="immo-row" style={{
          display: 'grid', gridTemplateColumns: LIST_COLS,
          padding: '18px 28px', alignItems: 'center',
          borderBottom: i < filtered.length - 1 ? `1px solid ${BORDER}` : 'none',
        }}>
          <div style={{ fontSize: '12px', color: MUTED }}>{fmt(c.cost_date)}</div>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '-0.2px' }}>{propertyName(c.property_id)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{ width: '8px', height: '8px', background: CATEGORY_COLOR[c.category] || MUTED, flexShrink: 0 }} />
            <span style={{ fontSize: '12px' }}>{c.category}</span>
          </div>
          <div style={{ fontSize: '13px', fontWeight: 700 }}>{fmtEur(c.amount)}</div>
          <div style={{ fontSize: '12px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes || '–'}</div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: BLUE, padding: 0, fontFamily: 'inherit' }}>Bearb.</button>
            <button onClick={() => deleteCost(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: MUTED, padding: 0, fontFamily: 'inherit' }}>Löschen</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnnualView({ annualData, properties }) {
  const { year, byProperty } = annualData;
  const entries = Object.entries(byProperty);

  if (entries.length === 0) {
    return (
      <div style={{ padding: '64px', textAlign: 'center', border: `1px solid ${BORDER}`, background: 'white' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: MUTED }}>
          Keine Daten für {year}
        </span>
      </div>
    );
  }

  const grandTotal = entries.reduce((s, [, d]) => s + d.total, 0);

  return (
    <div>
      <div style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.5px', marginBottom: '20px' }}>
        Jahresabrechnung <strong style={{ color: INK }}>{year}</strong>
        &nbsp;·&nbsp; Gesamtkosten: <strong style={{ color: INK }}>{fmtEur(grandTotal)}</strong>
      </div>
      <div style={{ display: 'grid', gap: '2px' }}>
        {entries.map(([pid, data]) => {
          const name = pid === '__none__' ? 'Kein Objekt' : (properties.find(p => p.id === pid)?.name || pid);
          const categoryEntries = Object.entries(data.byCategory).sort((a, b) => b[1] - a[1]);
          const share = grandTotal > 0 ? (data.total / grandTotal * 100).toFixed(1) : 0;

          return (
            <div key={pid} style={{ border: `1px solid ${BORDER}`, background: 'white' }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '-0.2px' }}>{name}</span>
                  <span style={{ fontSize: '11px', color: MUTED }}>{share}% der Gesamtkosten</span>
                </div>
                <span style={{ fontSize: '15px', fontWeight: 900, letterSpacing: '-0.5px' }}>{fmtEur(data.total)}</span>
              </div>
              <div style={{ height: '3px', background: BORDER }}>
                <div style={{ height: '100%', width: `${share}%`, background: INK, transition: 'width 0.4s' }} />
              </div>
              <div style={{ padding: '16px 24px' }}>
                {categoryEntries.map(([cat, amt]) => {
                  const catShare = data.total > 0 ? (amt / data.total * 100).toFixed(0) : 0;
                  return (
                    <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
                      <div style={{ width: '8px', height: '8px', background: CATEGORY_COLOR[cat] || MUTED, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '12px' }}>{cat}</span>
                      <span style={{ fontSize: '11px', color: MUTED, width: '36px', textAlign: 'right' }}>{catShare}%</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, width: '100px', textAlign: 'right' }}>{fmtEur(amt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
